import { useState, type FormEvent } from 'react';
import PageLayout from '@/components/feature/PageLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { aboutInfo } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9sngbl8o1vn02ekuifg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg.toLowerCase().includes('spam')) {
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      } else {
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <PageLayout>
      <SEOMeta
        title="关于我们 – 挠脚心vk内容聚合平台"
        description="了解挠脚心vk内容聚合平台的品牌故事、使命愿景、用户口碑和联系方式。我们致力于为用户提供最全面的挠脚心vk内容聚合服务。"
        keywords="挠脚心vk,关于我们,品牌介绍,联系我们,平台介绍"
        canonicalUrl="https://naojiaoxinvk.com/about"
      />
      <SchemaOrg data={{
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们 – 挠脚心vk',
        description: '了解挠脚心vk内容聚合平台的品牌故事、使命愿景、用户口碑和联系方式。',
        url: 'https://naojiaoxinvk.com/about',
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: '挠脚心vk', url: 'https://naojiaoxinvk.com/' },
      }} />
      <SchemaOrg data={{
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naojiaoxinvk.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://naojiaoxinvk.com/about' },
        ],
      }} />

      <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14 text-center">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
          关于 <span className="text-primary-500">挠脚心vk</span>
        </h1>
        <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
          {aboutInfo.slogan}
        </p>
      </section>

      {/* Brand Introduction */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">品牌介绍</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">{aboutInfo.introduction}</p>
              <div className="space-y-3 mt-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-flag-line text-accent-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">我们的使命</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.mission}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-eye-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">我们的愿景</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.vision}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20digital%20content%20platform%20office%20with%20warm%20lighting%2C%20creative%20team%20collaboration%20concept%2C%20soft%20coral%20and%20cream%20aesthetic%2C%20minimalist%20workspace%20design%2C%20editorial%20photography%2C%20clean%20professional%20atmosphere&width=800&height=500&seq=about-brand-01&orientation=landscape"
                alt="挠脚心vk品牌"
                title="挠脚心vk品牌介绍"
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {[
            { number: '50万+', label: '月活跃用户', icon: 'ri-user-line' },
            { number: '10万+', label: '视频资源', icon: 'ri-video-line' },
            { number: '5万+', label: '社区帖子', icon: 'ri-chat-3-line' },
            { number: '2000+', label: '深度文章', icon: 'ri-article-line' },
          ].map((stat, i) => (
            <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-6 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className={`${stat.icon} text-accent-600 text-lg`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">{stat.number}</div>
              <div className="text-xs text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Reputation */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 text-center mb-8">用户口碑</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {aboutInfo.reputation.map((item, i) => (
              <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5">
                <div className="flex items-center gap-1 mb-2">
                  {Array.from({ length: item.rating }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                  {Array.from({ length: 5 - item.rating }).map((_, j) => (
                    <i key={j} className="ri-star-line text-foreground-300 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 mb-3 leading-relaxed">&ldquo;{item.comment}&rdquo;</p>
                <span className="text-xs text-foreground-500 font-medium">— {item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 text-center mb-8">联系我们</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto">
          {[
            { icon: 'ri-mail-line', label: '电子邮箱', value: aboutInfo.contact.email, href: `mailto:${aboutInfo.contact.email}` },
            { icon: 'ri-wechat-line', label: '微信', value: aboutInfo.contact.wechat, href: '#' },
            { icon: 'ri-map-pin-line', label: '地址', value: aboutInfo.contact.address, href: '#' },
          ].map((item, i) => (
            <a key={i} href={item.href} className="block bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5 text-center hover:border-primary-200 transition-colors cursor-pointer" rel={item.href.startsWith('mailto:') ? 'nofollow' : undefined}>
              <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className={`${item.icon} text-primary-600 text-lg`}></i>
              </div>
              <h3 className="text-sm font-medium text-foreground-900 mb-1">{item.label}</h3>
              <p className="text-xs text-foreground-600">{item.value}</p>
            </a>
          ))}
        </div>
      </section>

      {/* Contact Form */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-[640px] mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 text-center mb-2">发送消息</h2>
          <p className="text-sm text-foreground-500 text-center mb-6">有任何问题或建议，欢迎给我们留言</p>
          <form onSubmit={handleSubmit} className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 space-y-4" data-readdy-form="true">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
              <input type="text" id="name" name="name" required className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
              <input type="email" id="email" name="email" required className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
              <textarea id="message" name="message" rows={4} maxLength={500} required className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"></textarea>
              <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
            </div>
            <div className="anti-spam-field">
              <label htmlFor="website_alt">备用网站</label>
              <input type="text" id="website_alt" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>
            <button type="submit" className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              发送留言
            </button>
            {formStatus === 'success' && (
              <p className="text-sm text-green-600 text-center mt-2">留言已成功发送，我们会尽快回复您！</p>
            )}
            {formStatus === 'error' && (
              <p className="text-sm text-red-500 text-center mt-2">{formError}</p>
            )}
          </form>
        </div>
      </section>
    </PageLayout>
  );
}